import React, { useEffect, useMemo, useState } from 'react';
import { portfolioCategories } from '../../mock';
import { ArrowRight, ExternalLink, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import { fetchCaseStudies } from '../../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useI18n } from '../../i18n';

const Portfolio = ({ limit = 0, showViewAll = true }) => {
  const { t } = useI18n();
  const [active, setActive] = useState(t('common.all'));
  const [industry, setIndustry] = useState('all');
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchCaseStudies().then(setItems).catch(() => setItems([]));
  }, []);

  const cats = [t('common.all'), ...portfolioCategories];

  const industries = useMemo(() => {
    const set = new Set(items.map((i) => i.industry).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (active !== t('common.all')) list = list.filter((i) => i.category === active);
    if (industry !== 'all') list = list.filter((i) => i.industry === industry);
    return limit > 0 ? list.slice(0, limit) : list;
  }, [active, industry, items, t, limit]);

  return (
    <section id="portfolio" className="py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-orange-600">{t('portfolio.kicker')}</span>
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{t('portfolio.title')}</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {cats.map((c) => (
              <button key={c} onClick={() => setActive(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  active === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                }`}>{c}</button>
            ))}
            <div className="flex items-center gap-2 ml-1">
              <Filter className="h-4 w-4 text-slate-500" />
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger className="h-10 w-44"><SelectValue placeholder={t('common.industryLabel')} /></SelectTrigger>
                <SelectContent>
                  {industries.map((i) => (<SelectItem key={i} value={i}>{i === 'all' ? t('common.allIndustries') : i}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {filtered.length === 0 && (
          <div className="mt-12 text-center text-slate-500 py-14 border border-dashed border-slate-200 rounded-2xl">
            {t('portfolio.empty')}
          </div>
        )}

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <Link to={`/portfolio/${p.slug}`} key={p.slug} className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 bg-white/95 text-slate-800 text-[11px] font-semibold px-2.5 py-1 rounded">{p.industry}</span>
                <span className="absolute bottom-3 left-3 bg-orange-500 text-white text-[11px] font-semibold px-2.5 py-1 rounded">{p.outcome_headline}</span>
              </div>
              <div className="p-5 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors">{p.title}</h3>
                <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-orange-500 transition-colors" />
              </div>
            </Link>
          ))}
        </div>

        {showViewAll && (
          <div className="mt-12 flex justify-center">
            <Link to="/portfolio" data-testid="view-all-portfolio-btn">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-6">{t('common.viewAllPortfolio')} <ArrowRight className="h-4 w-4 ml-2" /></Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Portfolio;
